import {v4 as uuidv4} from "uuid";
import {http, HttpResponse} from "msw";
import {waitTime} from "@shared/mocks/wait-time";

// =============================================================================
// Мок раздела "Графики" (доски пользовательских графиков + исторические данные
// по метрикам, GET /metrics/:metric_id/interval). Доски и метрики построены на
// реальном составе оборудования демонстрационного объекта №1 (см. stationMock/MQTT-METRICS.md §9).
// =============================================================================

function round(value: number, precision = 1): number {
    const p = Math.pow(10, precision);
    return Math.round(value * p) / p;
}

/** Равномерная сетка из `count` меток времени на интервале [start, end] — так же, как это делал бы бэкенд, отдавая не больше `threshold` точек. */
function timeGrid(start: number, end: number, threshold: number): number[] {
    const n = Math.max(2, Math.min(2000, Math.round(threshold) || 100));
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
        const now = Date.now();
        return timeGrid(now - 24 * 3_600_000, now, n);
    }
    const step = (end - start) / (n - 1);
    return new Array(n).fill(0).map((_, i) => Math.round(start + i * step));
}

/** Автокоррелированный шум AR(1) в коридоре ±amplitude вокруг base (§3.2 MQTT-METRICS.md) */
function noisyWalk(base: number, count: number, amplitude = 0.03, k = 0.3): number[] {
    const values: number[] = [];
    let s = Math.random() * 2 - 1;
    for (let i = 0; i < count; i++) {
        s = (1 - k) * s + k * (Math.random() * 2 - 1);
        values.push(base * (1 + amplitude * s));
    }
    return values;
}

const HOURLY_FACTORS = [
    0.45, 0.35, 0.30, 0.30, 0.35, 0.50, 0.85, 1.35,
    1.75, 1.55, 1.40, 1.25, 1.15, 1.10, 1.05, 1.05,
    1.10, 1.25, 1.40, 1.45, 1.35, 1.15, 0.90, 0.65,
];

/** Суточный профиль расхода: значение зависит от часа суток каждой метки времени (§2.6) */
function diurnal(hourlyAverage: number, timestamps: number[], amplitude = 0.08): number[] {
    let s = Math.random() * 2 - 1;
    return timestamps.map(ts => {
        const hour = new Date(ts).getHours();
        s = 0.8 * s + 0.2 * (Math.random() * 2 - 1);
        return hourlyAverage * HOURLY_FACTORS[hour] * (1 + amplitude * s);
    });
}

/** Циклический процесс (аэрация/денитрификация): чередование двух уровней по фазе, привязанной к unix-времени */
function cyclePhases(valueEven: number, valueOdd: number, periodMs: number, timestamps: number[], amplitude = 0.05): number[] {
    let s = 0;
    return timestamps.map(ts => {
        const phase = Math.floor(ts / periodMs) % 2 === 0;
        s = 0.7 * s + 0.3 * (Math.random() * 2 - 1);
        return (phase ? valueEven : valueOdd) * (1 + amplitude * s);
    });
}

/** Циклограмма привода: работа duty·period, пауза (1-duty)·period, привязана к unix-времени (§2.2) */
function dutyCycle(timestamps: number[], periodMs: number, duty = 0.5): number[] {
    return timestamps.map(ts => (ts % periodMs) / periodMs < duty ? 1 : 0);
}

/** Ток привода: совпадает с циклограммой, во включённом состоянии — шум вокруг базового значения */
function currentFollowingDuty(base: number, timestamps: number[], periodMs: number, duty = 0.5, amplitude = 0.02): number[] {
    const states = dutyCycle(timestamps, periodMs, duty);
    const noise = noisyWalk(base, timestamps.length, amplitude);
    return states.map((on, i) => on ? noise[i] : 0);
}

/** Монотонный счётчик (наработка, накопленный объём, энергия) */
function counter(startValue: number, ratePerHour: number, timestamps: number[]): number[] {
    const t0 = timestamps[0];
    return timestamps.map(ts => startValue + ratePerHour * (ts - t0) / 3_600_000);
}

/** Забивание фильтра между промывками: пилообразное падение давления с периодическим сбросом (§5.4) */
function decliningSawtooth(base: number, drop: number, periodMs: number, timestamps: number[], noise = 0.01): number[] {
    return timestamps.map(ts => {
        const frac = (ts % periodMs) / periodMs;
        const n = 1 + noise * (Math.random() * 2 - 1);
        return (base - drop * frac) * n;
    });
}

// ---------- Каталог метрик для графиков ----------

interface MetricCatalogEntry {
    id: string;
    name: string;
    localName: string;
    unit: string;
    precision: number;
    gen: (timestamps: number[]) => number[];
}

const catalog: Record<string, MetricCatalogEntry> = {};

function registerMetric(name: string, localName: string, unit: string, precision: number, gen: (timestamps: number[]) => number[]): { id: string; name: string; unit: string } {
    const id = uuidv4();
    catalog[id] = {id, name, localName, unit, precision, gen};
    return {id, name, unit};
}

const flowInlet = registerMetric("FLOW_RATE_INLET", "Расход сточных вод на входе", "CUBIC_METER_PER_HOUR", 1,
    ts => diurnal(41.7, ts));
const flowOutlet = registerMetric("FLOW_RATE_OUTLET", "Расход сточных вод на выходе", "CUBIC_METER_PER_HOUR", 1,
    ts => diurnal(40.4, ts));
const tempInlet = registerMetric("TEMPERATURE_INLET", "Температура сточных вод на входе", "C", 1,
    ts => noisyWalk(16, ts.length, 0.05));
const tempAmbient = registerMetric("TEMPERATURE_AMBIENT", "Температура наружного воздуха", "C", 1,
    ts => noisyWalk(19, ts.length, 0.2, 0.15));
const surgeLevel = registerMetric("LEVEL", "Уровень в усреднителе", "METER", 2,
    ts => noisyWalk(2.15, ts.length, 0.08));

const bioPh1 = registerMetric("PH", "pH, линия биоочистки №1", "NONE", 2,
    ts => cyclePhases(6.95, 7.35, 1_440_000, ts, 0.015));
const bioOxygen1 = registerMetric("DISSOLVED_OXYGEN", "Растворённый кислород, линия №1", "MILLIGRAM_PER_LITER", 2,
    ts => cyclePhases(2.4, 0.2, 1_440_000, ts, 0.12));
const recyclePumpCurrentA = registerMetric("CURRENT_CONSUMPTION_A", "Насос рециркуляции №1 — ток фазы A", "MA", 0,
    ts => currentFollowingDuty(2750, ts, 3_600_000, 0.6));

const blowerPressure = registerMetric("OVERPRESSURE", "Давление в коллекторе воздуходувного модуля №1", "MBAR", 0,
    ts => noisyWalk(290, ts.length, 0.03));
const blower1CurrentA = registerMetric("CURRENT_CONSUMPTION_A", "Воздуходувка №1 — ток фазы A", "MA", 0,
    ts => noisyWalk(36800, ts.length, 0.02));
const activePowerTotal = registerMetric("ACTIVE_POWER", "Активная мощность станции", "KILOWATT", 2,
    ts => diurnal(72, ts, 0.1));
const energyTotal = registerMetric("ENERGY", "Общее энергопотребление станции", "KILOWATT_HOUR", 1,
    ts => counter(245_830, 53, ts));

const filterPressure = registerMetric("OVERPRESSURE", "Давление после механического фильтра доочистки", "MBAR", 0,
    ts => decliningSawtooth(1180, 140, 14 * 86_400_000, ts));
const dispenserState = registerMetric("STATE", "Дозатор гипохлорита натрия №1", "NONE", 0,
    ts => dutyCycle(ts, 1_800_000, 0.3));
const uvLampRunningTime = registerMetric("RUNNING_TIME", "Наработка УФ-лампы узла доочистки №1", "HOUR", 1,
    ts => counter(6120, 0.97, ts));

// ---------- Доски и графики ----------

function line(metric: { id: string; name: string; unit: string }, color: string, type: "LINEAR" | "STEPPED" = "LINEAR") {
    return {id: uuidv4(), name: metric.name, type, metric_id: metric.id, color, unit: metric.unit};
}

function chart(name: string, lines: ReturnType<typeof line>[], type = "line") {
    return {id: uuidv4(), name, type, lines};
}

function board(name: string, charts: ReturnType<typeof chart>[]) {
    return {id: uuidv4(), name, charts};
}

const boards: Record<string, any> = Object.fromEntries([
    board("Приток и качество стока", [
        chart("Расход сточных вод, вход/выход", [
            line(flowInlet, "#008cc7"),
            line(flowOutlet, "#2ecc71"),
        ]),
        chart("Температура", [
            line(tempInlet, "#e67e22"),
            line(tempAmbient, "#9b59b6"),
        ]),
        chart("Уровень в усреднителе", [
            line(surgeLevel, "#3498db"),
        ]),
    ]),
    board("Биологическая очистка — линия №1", [
        chart("pH и растворённый кислород", [
            line(bioPh1, "#16a085"),
            line(bioOxygen1, "#f39c12"),
        ]),
        chart("Ток насоса рециркуляции", [
            line(recyclePumpCurrentA, "#c0392b", "STEPPED"),
        ]),
    ]),
    board("Электроснабжение и воздуходувки", [
        chart("Давление и ток воздуходувки №1", [
            line(blowerPressure, "#2980b9"),
            line(blower1CurrentA, "#d35400"),
        ]),
        chart("Мощность и энергопотребление станции", [
            line(activePowerTotal, "#8e44ad"),
            line(energyTotal, "#27ae60"),
        ]),
    ]),
    board("Доочистка и реагенты", [
        chart("Давление после фильтров (забивание/промывка)", [
            line(filterPressure, "#c0392b"),
        ]),
        chart("Дозатор ГХН и УФ-лампа", [
            line(dispenserState, "#2c3e50", "STEPPED"),
            line(uvLampRunningTime, "#7f8c8d"),
        ]),
    ]),
].map(b => [b.id, b]));

// ---------- HTTP-хендлеры (MSW) ----------

const apiUrl = "*/api/v2";
const boardUrl = apiUrl + "/chart-board";
const intervalUrl = apiUrl + "/metrics/:metric_id/interval";

const getAllBoards = http.get(boardUrl, async () => {
    await waitTime(500);
    const nameById = Object.fromEntries(Object.values(boards).map(b => [b.id, b.name]));
    return HttpResponse.json({boards: nameById});
});

const getOneBoard = http.get(`${boardUrl}/:id`, async ({params}) => {
    const id = params["id"] as string;

    await waitTime(500);
    return HttpResponse.json(boards[id] ?? null, {status: boards[id] ? 200 : 404});
});

const getMetricInterval = http.get(intervalUrl, async ({params, request}) => {
    await waitTime(300);

    const metricId = params["metric_id"] as string;
    const url = new URL(request.url);
    const start = Number(url.searchParams.get("start"));
    const end = Number(url.searchParams.get("end"));
    const threshold = Number(url.searchParams.get("threshold"));

    const timestamps = timeGrid(start, end, threshold);
    const known = catalog[metricId];

    if (known) {
        const values = known.gen(timestamps);
        return HttpResponse.json({
            id: known.id,
            name: known.name,
            local_name: known.localName,
            unit: known.unit,
            events: timestamps.map((timestamp, i) => ({timestamp, value: round(values[i], known.precision)})),
        });
    }

    // Неизвестная метрика (выбрана через дерево устройств станции, не из каталога
    // графиков) — отдаём правдоподобный, но обобщённый ряд, чтобы график не падал.
    const values = noisyWalk(50, timestamps.length, 0.15);
    return HttpResponse.json({
        id: metricId,
        name: "METRIC",
        local_name: "Показатель",
        unit: "NONE",
        events: timestamps.map((timestamp, i) => ({timestamp, value: round(values[i], 1)})),
    });
});

export const chartsHandlers = [
    getAllBoards,
    getOneBoard,
    getMetricInterval,
];
